<template>
  <div class="header">
    <div class="container-fluid">
      <!-- Header Section -->
      <div class="card">
        <div class="card-header">
          <h4 class="card-header-title">Certificate</h4>
          <b-button variant="danger" @click="addCertificate" class="btn-pill">
            <span class="fe fe-file-plus"></span>
            Add Certificate
          </b-button>
        </div>

        <!-- Filter Section -->
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <v-select
                v-model="filters.type"
                :options="typeOptions"
                placeholder="Select Type"
                @input="onFilterChange">
              </v-select>
            </div>
            <div class="col-md-6">
              <v-select
                v-model="filters.category"
                :options="categoryOptions"
                label="title"
                :reduce="option => option.id"
                placeholder="Select Category"
                @input="onFilterChange">
              </v-select>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-4 offset-6">
              <b-form-input
                v-model="filters.search"
                placeholder="Search Here..."
                @input="onSearch">
              </b-form-input>
            </div>
            <div class="col-md-2 ">
              <b-button variant="outline-primary" @click="clearFilter">
                <span class="fe fe-x-square"></span>
                Clear Filter
              </b-button>
            </div>
          </div>
        </div>

        <!-- Certificate Grid -->
        <div class="card-body">
          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading certificates...</p>
          </div>
          
          <div v-else-if="certificates.length === 0" class="text-center py-5">
            <div class="empty-state">
              <i class="fe fe-file-text display-4 text-muted"></i>
              <h5 class="mt-3">No certificates found</h5>
              <p class="text-muted">Try adjusting your filters or add a new certificate</p>
            </div>
          </div>

          <div v-else class="row">
            <div v-for="certificate in certificates" :key="certificate.id" class="col-lg-4 col-md-6 mb-4">
              <div class="card certificate-card h-100">
                <div class="card-body">
                  <!-- Certificate Preview -->
                  <div class="certificate-preview mb-3">
                    <img
                      v-if="certificate.image_url"
                      :src="`${imageBaseUrl}/attachments/${certificate.image_url}`"
                      class="certificate-image"
                      @click="viewCertificate(certificate)"
                      style="cursor: pointer;">
                    <div v-else class="certificate-placeholder" @click="viewCertificate(certificate)">
                      <i class="fe fe-file-text display-4 text-muted"></i>
                      <p class="text-muted mt-2">No preview available</p>
                    </div>
                  </div>

                  <!-- Certificate Info -->
                  <!-- <h6 class="card-title">{{ certificate.name || 'Untitled Certificate' }}</h6> -->
                  
                  <!-- Type and Category Badges -->
                  <div>
                    <span class="badge badge-pill mr-2" :class="getTypeBadgeClass(certificate.type)">
                      {{ certificate.type }}
                    </span>
                    <span class="badge badge-pill badge-secondary">
                      {{ certificate.training_category.title }}
                    </span>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="card-footer bg-transparent">
                  <span>Action</span>
                  <div class="card-action">
                    <b-button 
                      variant="info" 
                      size="sm" 
                      @click="viewCertificate(certificate)"
                      title="View Certificate">
                      <i class="fe fe-eye"></i>
                    </b-button>
                    <b-button 
                      variant="warning" 
                      size="sm" 
                      @click="editCertificate(certificate)"
                      title="Edit Certificate">
                      <i class="fe fe-edit"></i>
                    </b-button>
                    <b-button 
                      variant="danger" 
                      size="sm" 
                      @click="deleteCertificate(certificate)"
                      title="Delete Certificate">
                      <i class="fe fe-trash"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination Footer -->
          <div v-if="!isLoading && certificates.length > 0" class="mt-4 pt-3 border-top">
            <div class="row align-items-center">
              <div class="col-md-4">
                <div class="d-flex align-items-center">
                  <span class="text-muted small me-2">Show</span>
                  <select 
                    v-model="body.limit" 
                    class="form-select form-select-sm" 
                    style="width: 70px;"
                    @change="onPerPageChange">
                    <option v-for="option in perPageOptions" :key="option" :value="option">{{ option }}</option>
                  </select>
                  <span class="text-muted small ms-2">entries</span>
                </div>
              </div>
              <div class="col-md-4 text-center">
                <p class="text-muted small mb-0">
                  Showing {{ showingFrom }} to {{ showingTo }} of {{ totalItems }} entries
                </p>
              </div>
              <div class="col-md-4">
                <nav aria-label="Certificate pagination">
                  <ul class="pagination pagination-sm justify-content-end mb-0">
                    <!-- Previous Button -->
                    <li class="page-item" :class="{ disabled: body.page <= 1 }">
                      <button 
                        class="page-link" 
                        @click="changePage(body.page - 1)"
                        :disabled="body.page <= 1">
                        <i class="fe fe-chevron-left"></i>
                      </button>
                    </li>
                    
                    <!-- Page Numbers -->
                    <template v-for="(page, index) in visiblePages">
                      <!-- Show dots -->
                      <li 
                        v-if="page === '...'" 
                        :key="'dots-' + index"
                        class="page-item disabled">
                        <span class="page-link">...</span>
                      </li>
                      <!-- Show page number -->
                      <li 
                        v-else
                        :key="page"
                        class="page-item"
                        :class="{ active: page === body.page }">
                        <button 
                          class="page-link" 
                          @click="changePage(page)">
                          {{ page }}
                        </button>
                      </li>
                    </template>
                    
                    <!-- Next Button -->
                    <li class="page-item" :class="{ disabled: body.page >= totalPages }">
                      <button 
                        class="page-link" 
                        @click="changePage(body.page + 1)"
                        :disabled="body.page >= totalPages">
                        <i class="fe fe-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Add/Edit Certificate Modal -->
      <b-modal
        id="modal-certificate"
        :title="modalTitle"
        size="xl"
        hide-footer
        centered
        @hide="resetForm">
        <div class="row">
          <!-- Certificate Detail Section -->
          <div class="col-md-6">
            <h5>Certificate Detail</h5>
            
            <!-- Type -->
            <div class="form-group">
              <label>Type <span class="text-danger">*</span></label>
              <v-select
                v-if="isAdd"
                v-model="bodyAdd.type"
                :options="typeOptions"
                placeholder="Select Type"
                :class="{ 'is-invalid': formErrors.type }">
              </v-select>
              <v-select
                v-else
                v-model="bodyUpdate.type"
                :options="typeOptions"
                placeholder="Select Type"
                :class="{ 'is-invalid': formErrors.type }">
              </v-select>
              <div v-if="formErrors.type" class="invalid-feedback d-block">
                {{ formErrors.type }}
              </div>
            </div>

            <!-- Category -->
            <div class="form-group">
              <label>Category <span class="text-danger">*</span></label>
              <v-select
                v-if="isAdd"
                v-model="bodyAdd.training_category_id"
                :options="categoryOptions"
                label="title"
                :reduce="option => option.id"
                placeholder="Select Category"
                :class="{ 'is-invalid': formErrors.category }">
              </v-select>
              <v-select
                v-else
                v-model="bodyUpdate.training_category_id"
                :options="categoryOptions"
                label="title"
                :reduce="option => option.id"
                placeholder="Select Category"
                :class="{ 'is-invalid': formErrors.category }">
              </v-select>
              <div v-if="formErrors.category" class="invalid-feedback d-block">
                {{ formErrors.category }}
              </div>
            </div>

            <!-- Validity Period -->
            <!-- <div class="form-group">
              <label>Validity Period</label>
              <div class="row">
                <div class="col-md-6">
                  <label class="form-label">Year (0-5)</label>
                  <v-select
                    v-if="isAdd"
                    v-model="bodyAdd.validity_year"
                    :options="yearOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Select Year"
                    @input="onYearChange('add')"
                    :class="{ 'is-invalid': formErrors.validity_period }">
                  </v-select>
                  <v-select
                    v-else
                    v-model="bodyUpdate.validity_year"
                    :options="yearOptions"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Select Year"
                    @input="onYearChange('update')"
                    :class="{ 'is-invalid': formErrors.validity_period }">
                  </v-select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Month (0-12)</label>
                  <v-select
                    v-if="isAdd"
                    v-model="bodyAdd.validity_month"
                    :options="getMonthOptions('add')"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Select Month"
                    :disabled="isMonthDisabled('add')"
                    @input="onMonthChange('add')"
                    :class="{ 'is-invalid': formErrors.validity_period }">
                  </v-select>
                  <v-select
                    v-else
                    v-model="bodyUpdate.validity_month"
                    :options="getMonthOptions('update')"
                    label="label"
                    :reduce="option => option.value"
                    placeholder="Select Month"
                    :disabled="isMonthDisabled('update')"
                    @input="onMonthChange('update')"
                    :class="{ 'is-invalid': formErrors.validity_period }">
                  </v-select>
                </div>
              </div>
              <div v-if="formErrors.validity_period" class="invalid-feedback d-block">
                {{ formErrors.validity_period }}
              </div>
              <small class="text-muted">
                Maximum: 5 years. Total selected: 
                <span v-if="isAdd">{{ getTotalMonths('add') }} months</span>
                <span v-else>{{ getTotalMonths('update') }} months</span>
              </small>
            </div> -->
          </div>

          <!-- Preview Section -->
          <div class="col-md-6">
            <h5>Preview</h5>
            <div class="upload-area">
              <div v-if="previewUrl || (!isAdd && bodyUpdate.image_url)" class="preview-container">
                <img
                  :src="previewUrl || `${imageBaseUrl}/attachments/${bodyUpdate.image_url}`"
                  alt="Certificate Preview"
                  class="preview-image">
                <b-button 
                  variant="outline-danger" 
                  size="sm" 
                  class="remove-preview-btn"
                  @click="removeFile">
                  <i class="fe fe-x"></i>
                </b-button>
              </div>
              <div v-else class="drop-zone" @drop="handleDrop" @dragover.prevent @dragenter.prevent>
                <div class="drop-zone-content">
                  <i class="fe fe-upload display-4 text-muted"></i>
                  <h6 class="mt-3">Drag and Drop the Document</h6>
                  <p class="text-muted">Upload file .jpg,.jpeg,.png (Max. Size 2MB)</p>
                  <b-button variant="danger" @click="chooseFile">
                    Choose File
                  </b-button>
                </div>
              </div>
              <input
                ref="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png"
                @change="handleFileSelect"
                class="d-none">
              <div v-if="formErrors.file" class="invalid-feedback d-block mt-2">
                {{ formErrors.file }}
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="mt-4 d-flex justify-content-end">
          <b-button variant="secondary" class="me-2" @click="$bvModal.hide('modal-certificate')">
            Cancel
          </b-button>
          <b-button 
            variant="danger" 
            @click="saveCertificate"
            :disabled="isSubmitting">
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ isEdit ? 'Update' : 'Submit' }}
          </b-button>
        </div>
      </b-modal>

      <!-- Certificate Preview Modal -->
      <b-modal
        id="modal-preview"
        title="Certificate Preview"
        size="xl"
        hide-footer
        centered>
        <div v-if="selectedCertificate" class="text-center">
          <img 
            v-if="selectedCertificate.image_url"
            :src="`${imageBaseUrl}/attachments/${selectedCertificate.image_url}`"
            :alt="selectedCertificate.name"
            class="img-fluid certificate-fullscreen">
          <div v-else class="py-5">
            <i class="fe fe-file-text display-1 text-muted"></i>
            <h5 class="mt-3">No preview available</h5>
            <p class="text-muted">The certificate file cannot be previewed</p>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
  import logic from './certificate.logic.js';
  
  export default {
    ...logic,
  };
</script>

<style scoped src="./certificate.css"></style>
